// TelcoTrack — expand object metadata to one row per excavation surface (VYKOP list).

import type {
  ExcavationSurfaceSnapshotRow,
  PathAdvanceBatchStreetAssignment,
  VykopSurfaceListRow,
} from '../interfaces/path-advance-batch.interface';
import type { VykopSegmentKind } from '../constants/vykop-segment-kind.constants';
import {
  getExcavationMaterialName,
  getExcavationSubtypeName,
} from '../constants/excavation-material.constants';

function readMetaRecord(metadata: unknown): Record<string, unknown> {
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? (metadata as Record<string, unknown>)
    : {};
}

function pickNum(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Parse `metadata.excavationSurfaces[]` (+ legacy root scalars) into normalized rows. */
export function parseExcavationSurfaceSnapshotsFromMetadata(
  metadata: unknown,
): ExcavationSurfaceSnapshotRow[] {
  const meta = readMetaRecord(metadata);
  const out: ExcavationSurfaceSnapshotRow[] = [];
  const es = meta['excavationSurfaces'];
  if (Array.isArray(es)) {
    es.forEach((row, i) => {
      if (!row || typeof row !== 'object') return;
      const rec = row as Record<string, unknown>;
      const mat = String(rec['excavationMaterial'] ?? '').trim();
      const len = pickNum(rec['lengthM']);
      if (!mat && len == null) return;
      out.push({
        surfaceIndex: i + 1,
        excavationMaterial: mat,
        excavationMaterialSubtype: String(rec['excavationMaterialSubtype'] ?? '').trim() || undefined,
        lengthM: len,
        deepeningCm: pickNum(rec['deepeningCm']) ?? undefined,
      });
    });
  }
  if (out.length === 0) {
    const mat = String(meta['excavationMaterial'] ?? '').trim();
    const len = pickNum(meta['excavationLengthM']);
    if (mat || len != null) {
      out.push({
        surfaceIndex: 1,
        excavationMaterial: mat,
        excavationMaterialSubtype:
          String(meta['excavationMaterialSubtype'] ?? '').trim() || undefined,
        lengthM: len,
        deepeningCm: pickNum(meta['excavationDeepeningCm']) ?? undefined,
      });
    }
  }
  return out;
}

export interface BuildVykopSurfaceListRowsParams {
  groupKey: string;
  pathAdvanceBatchId: string | null;
  segmentId: string;
  segmentCount: number;
  status: string | null;
  vykopSegmentKind: VykopSegmentKind | null;
  metadata: unknown;
  street: PathAdvanceBatchStreetAssignment;
  streetObec?: string | null;
  streetPsc?: string | null;
  polygonId: string | null;
  updatedAt: string | null;
}

function formatSurfaceLengthM(lengthM: number | null | undefined): string {
  return lengthM != null && Number.isFinite(lengthM) ? `${lengthM} m` : '—';
}

/** Summary text for all surfaces on a lead segment — parity with web objects list „Povrchy“. */
export function formatExcavationSurfacesSummaryFromMetadata(metadata: unknown): string {
  const surfaces = parseExcavationSurfaceSnapshotsFromMetadata(metadata);
  if (surfaces.length === 0) return '—';
  const parts = surfaces.map((s) => {
    const mat = s.excavationMaterial?.trim();
    const materialLabel = mat ? getExcavationMaterialName(mat) : '—';
    const sub = s.excavationMaterialSubtype?.trim();
    const subtypeLabel = sub ? getExcavationSubtypeName(sub) : '—';
    const suffix = subtypeLabel !== '—' ? ` / ${subtypeLabel}` : '';
    return `${materialLabel}${suffix}: ${formatSurfaceLengthM(s.lengthM)}`;
  });
  return parts.join('; ');
}

/** Expand one batch/segment group into zero or more surface list rows. */
export function buildVykopSurfaceListRowsFromGroup(
  params: BuildVykopSurfaceListRowsParams,
): VykopSurfaceListRow[] {
  const surfaces = parseExcavationSurfaceSnapshotsFromMetadata(params.metadata);
  if (surfaces.length === 0) return [];

  return surfaces.map((s) => ({
    rowKey: `${params.groupKey}:${s.surfaceIndex}`,
    pathAdvanceBatchId: params.pathAdvanceBatchId,
    segmentId: params.segmentId,
    segmentCount: params.segmentCount,
    status: params.status,
    vykopSegmentKind: params.vykopSegmentKind,
    surfaceIndex: s.surfaceIndex,
    excavationMaterial: s.excavationMaterial,
    excavationMaterialSubtype: s.excavationMaterialSubtype,
    lengthM: s.lengthM,
    deepeningCm: s.deepeningCm,
    streetName: params.street.streetName,
    streetSlopePercent: params.street.streetSlopePercent,
    streetSource: params.street.streetSource,
    streetPending: params.street.streetPending,
    streetObec: params.streetObec ?? null,
    streetPsc: params.streetPsc ?? null,
    polygonId: params.polygonId,
    updatedAt: params.updatedAt,
  }));
}
